import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import apiClient from "../api";
import toast from "react-hot-toast";

/* ---------- Small utilities ---------- */
const PAGE_SIZES = [25, 50, 100, 200];

function useDebouncedValue(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function cls(...xs) {
  return xs.filter(Boolean).join(" ");
}

const SortButton = ({ label, field, sort, setSort }) => {
  const dir = sort?.startsWith("-") ? "desc" : sort?.startsWith("+") ? "asc" : null;
  const active = sort?.slice(1) === field;
  const next = () => {
    // cycle: none -> asc -> desc -> none
    if (!active) return setSort("+" + field);
    if (dir === "asc") return setSort("-" + field);
    if (dir === "desc") return setSort(null);
  };
  return (
    <button
      type="button"
      onClick={next}
      className={cls(
        "flex items-center gap-1 select-none",
        active ? "text-blue-700 font-semibold" : "text-gray-700"
      )}
      title={active ? (dir === "asc" ? "Sorted ascending" : "Sorted descending") : "Not sorted"}
    >
      <span>{label}</span>
      <span className="text-xs opacity-70">
        {!active ? "↕" : dir === "asc" ? "↑" : "↓"}
      </span>
    </button>
  );
};

/* ---------- Hour helpers ---------- */
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => ({
  value: String(h).padStart(2, "0"),
  label: new Date(2000, 0, 1, h).toLocaleTimeString([], { hour: "numeric" }),
}));

function computeHourWindowISO(dateStr, startHH, endHH) {
  if (!dateStr || startHH === "" || endHH === "") return null;
  const s = Number(startHH);
  const e = Number(endHH);
  if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) return null;

  const [y, m, d] = dateStr.split("-").map(Number);
  const from = new Date(y, m - 1, d, s, 0, 0, 0);
  const to = new Date(y, m - 1, d, e, 0, 0, 0);
  return { fromISO: from.toISOString(), toISO: to.toISOString(), from, to };
}

function safeDate(val) {
  if (!val) return null;
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? null : d;
}

/* ---------- Booking No helpers (NEW) ---------- */
function buildRB(dateStr, seq) {
  if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return null;
  const dayKey = dateStr.replaceAll("-", ""); // YYYYMMDD
  const raw = String(seq || "").replace(/\D/g, "");
  if (!raw) return `RB${dayKey}`;
  return `RB${dayKey}${raw.padStart(4, "0")}`;
}
function normalizeRBInput(s) {
  if (!s) return null;
  const t = String(s).trim().toUpperCase();
  if (!/^RB/.test(t)) return null;
  const m = t.match(/^RB(\d{8})(\d{1,4})?$/);
  if (!m) return t; // allow partial like RB2025
  const [, dayKey, seq] = m;
  if (!seq) return `RB${dayKey}`;
  return `RB${dayKey}${String(seq).padStart(4, "0")}`;
}

/* ---------- Stats helpers ---------- */
function formatMoney(v) {
  const n = Number(v || 0);
  return `Rs. ${n.toLocaleString()}`;
}

const StatCard = ({ label, value, sub, tone = "gray" }) => {
  const toneMap = {
    gray: "border-gray-200 bg-white text-gray-900",
    green: "border-green-200 bg-green-50/60 text-green-900",
    yellow: "border-yellow-200 bg-yellow-50/60 text-yellow-900",
    blue: "border-blue-200 bg-blue-50/60 text-blue-900",
    purple: "border-purple-200 bg-purple-50/60 text-purple-900",
  };

  return (
    <div className={cls("rounded-xl border shadow-sm p-4", toneMap[tone] || toneMap.gray)}>
      <div className="text-xs font-medium uppercase tracking-wide opacity-75">{label}</div>
      <div className="mt-2 text-2xl font-bold">{value}</div>
      {sub ? <div className="mt-1 text-xs opacity-70">{sub}</div> : null}
    </div>
  );
};

/* ---------- Badge UI ---------- */
const Badge = ({ label, variant = "gray" }) => {
  const styles = {
    gray: "bg-gray-100 text-gray-700",
    green: "bg-green-100 text-green-700",
    yellow: "bg-yellow-100 text-yellow-700",
    blue: "bg-blue-100 text-blue-700",
    red: "bg-red-100 text-red-700",
    purple: "bg-purple-100 text-purple-700",
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[variant]}`}>
      {label}
    </span>
  );
};

/* ---------- Badge Mappers ---------- */
const getPaymentBadge = (status) => {
  switch (status) {
    case "Paid":
      return <Badge label="Paid" variant="green" />;
    case "Pending":
      return <Badge label="Pending" variant="yellow" />;
    case "Manual":
      return <Badge label="Manual" variant="blue" />;
    case "PaidToOperator":
      return <Badge label="PaidToOperator" variant="purple" />;
    default:
      return <Badge label={status || "-"} variant="gray" />;
  }
};

const getStatusBadge = (status) => {
  switch (status) {
    case "Paid":
      return <Badge label="Paid" variant="green" />;
    case "Pending":
      return <Badge label="Pending" variant="yellow" />;
    case "Manual":
      return <Badge label="Manual" variant="blue" />;
    case "PaidToOperator":
      return <Badge label="PaidToOperator" variant="purple" />;
    default:
      return <Badge label={status || "-"} variant="gray" />;
  }
};

/* ---------- Component ---------- */
const AdminBookings = () => {
  /* Filters */
  const [filter, setFilter] = useState({
    date: "",
    from: "",
    to: "",
    userEmail: "",
    status: "",
    paymentStatus: "",
    hourStart: "",
    hourEnd: "",
    timeBasis: "created",
  });
  const debouncedFilter = useDebouncedValue(filter, 500);

  // NEW: quick RB builder inputs
  const [bnDate, setBnDate] = useState("");
  const [bnSeq, setBnSeq] = useState("");

  /* Paging & sorting */
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sort, setSort] = useState("-createdAt"); // NEW BOOKINGS FIRST

  /* Data & UI */
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [serverTotalPages, setServerTotalPages] = useState(null);
  const [summary, setSummary] = useState({
    totalBookings: 0,
    paidBookings: 0,
    pendingBookings: 0,
    manualBookings: 0,
    paidToOperatorBookings: 0,
    totalRevenue: 0,
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    bookingId: null,
  });
  const [highlightedRowId, setHighlightedRowId] = useState(null);
  const [rescheduleLoading, setRescheduleLoading] = useState(false);

  /* Reschedule UI */
  const [rescheduleData, setRescheduleData] = useState(null);
  const [newDate, setNewDate] = useState("");
  const [newSeats, setNewSeats] = useState("");

  /* Cancel token */
  const abortRef = useRef(null);

  const timeWindow = useMemo(() => {
    const { date, hourStart, hourEnd } = debouncedFilter;
    return computeHourWindowISO(date, hourStart, hourEnd);
  }, [debouncedFilter]);

  const queryParams = useMemo(() => {
    const {
      date, from, to, userEmail, status, paymentStatus, timeBasis,
    } = debouncedFilter;

    const clean = {};
    if (date) clean.date = date;
    if (from) clean.from = from;
    if (to) clean.to = to;

    // IMPORTANT: if the input looks like an RB code, DO NOT send it as userEmail,
    // send it only as bookingNo to avoid backend email filter removing all rows.
    const rbGuess = normalizeRBInput(userEmail) || null;
    if (rbGuess) {
      clean.bookingNo = rbGuess;
    } else if (userEmail) {
      clean.userEmail = userEmail;
    }

    if (status) clean.status = status;
    if (paymentStatus) clean.paymentStatus = paymentStatus;

    if (timeWindow) {
      if ((timeBasis || "created") === "created") {
        clean.createdFrom = timeWindow.fromISO;
        clean.createdTo = timeWindow.toISO;
      } else {
        clean.departureFrom = timeWindow.fromISO;
        clean.departureTo = timeWindow.toISO;
      }
    }

    return {
      ...clean,
      page,
      pageSize,
      ...(sort ? { sort } : {}),
    };
  }, [debouncedFilter, page, pageSize, sort, timeWindow]);

  const fetchBookings = async () => {
    setLoading(true);
    setErr(null);

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("authToken") || "";

      const requestConfig = {
        params: queryParams,
        headers: token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : {},
        signal: controller.signal,
      };

      let res;
      try {
        res = await apiClient.get("/bookings/admin/bookings", requestConfig);
      } catch (_primaryErr) {
        // Fallback for environments/builds using a slightly different admin path
        res = await apiClient.get("/admin/bookings", requestConfig);
      }

      const data = res.data;

      if (data && Array.isArray(data.items)) {
        setRows(data.items);
        setTotal(typeof data.total === "number" ? data.total : 0);
        setServerTotalPages(typeof data.totalPages === "number" ? data.totalPages : null);
        setSummary({
          totalBookings: Number(data?.summary?.totalBookings || 0),
          paidBookings: Number(data?.summary?.paidBookings || 0),
          pendingBookings: Number(data?.summary?.pendingBookings || 0),
          manualBookings: Number(data?.summary?.manualBookings || 0),
          paidToOperatorBookings: Number(data?.summary?.paidToOperatorBookings || 0),
          totalRevenue: Number(data?.summary?.totalRevenue || 0),
        });
        if (typeof data.page === "number") setPage(data.page);
        if (typeof data.pageSize === "number") setPageSize(data.pageSize);
      } else if (Array.isArray(data)) {
        // legacy fallback only
        setRows(data);
        setTotal(data.length);
        setServerTotalPages(null);
        setSummary({
          totalBookings: data.length,
          paidBookings: 0,
          pendingBookings: 0,
          manualBookings: 0,
          paidToOperatorBookings: 0,
          totalRevenue: 0,
        });
      } else {
        setRows([]);
        setTotal(0);
        setServerTotalPages(null);
        setSummary({
          totalBookings: 0,
          paidBookings: 0,
          pendingBookings: 0,
          manualBookings: 0,
          paidToOperatorBookings: 0,
          totalRevenue: 0,
        });
      }
    } catch (e) {
      if (e.name === "CanceledError") return;
      console.error("Failed to fetch bookings", e);
      setErr(
        e?.response?.data?.message ||
          "Failed to fetch bookings."
      );
      setRows([]);
      setTotal(0);
      setServerTotalPages(null);
      setSummary({
        totalBookings: 0,
        paidBookings: 0,
        pendingBookings: 0,
        manualBookings: 0,
        paidToOperatorBookings: 0,
        totalRevenue: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  const refetchAfterUpdate = async () => {
    await fetchBookings();
  };

  const cancelBooking = async (id) => {
    setConfirmModal({ open: true, bookingId: id });
  };

  const handleConfirmCancel = async () => {
    const id = confirmModal.bookingId;
    if (!id) return;

    setConfirmModal({ open: false, bookingId: null });
    setActionLoading(id);

    try {
      await apiClient.delete(`/bookings/${id}`, {
        headers: {
          Authorization: `Bearer ${
            localStorage.getItem("token") || localStorage.getItem("authToken") || ""
          }`,
        },
      });
      setRows((prev) => prev.filter((bk) => bk._id !== id));
      toast.success("Booking cancelled");
      refetchAfterUpdate();
    } catch (err) {
      console.error("Failed to cancel booking", err);
      toast.error("Failed to cancel booking");
    } finally {
      setActionLoading(null);
    }
  };

  const confirmReschedule = async () => {
    if (!rescheduleData?._id) return;

    setRescheduleLoading(true);

    try {
      const updatedId = rescheduleData._id;

      await apiClient.put(
        `/admin/bookings/${rescheduleData._id}`,
        {
          date: newDate,
          selectedSeats: newSeats
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
            .map((s) => s.toUpperCase()),
        },
        {
          headers: {
            Authorization: `Bearer ${
              localStorage.getItem("token") || localStorage.getItem("authToken") || ""
            }`,
          },
        }
      );
      toast.success("Booking rescheduled");
      setRescheduleData(null);
      setNewDate("");
      setNewSeats("");
      await refetchAfterUpdate();
      setHighlightedRowId(updatedId);
      setTimeout(() => setHighlightedRowId(null), 1600);
    } catch (err) {
      console.error("Failed to reschedule", err);
      toast.error("Failed to reschedule");
    } finally {
      setRescheduleLoading(false);
    }
  };

  /* Derived */
  const totalPages = useMemo(() => {
    if (typeof serverTotalPages === "number" && serverTotalPages >= 0) {
      return Math.max(1, serverTotalPages);
    }
    return Math.max(1, Math.ceil(total / pageSize));
  }, [serverTotalPages, total, pageSize]);

  /* Render helpers */
  const HeaderCell = ({ label, field }) => (
    <th className="border px-3 py-2 bg-gray-100 sticky top-0 z-10">
      <SortButton label={label} field={field} sort={sort} setSort={setSort} />
    </th>
  );

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4">📄 All User Bookings</h2>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 mb-4">
        <StatCard
          label="Total Bookings"
          value={(summary.totalBookings || total || 0).toLocaleString()}
          sub="Current filtered results"
          tone="gray"
        />
        <StatCard
          label="Paid"
          value={(summary.paidBookings || 0).toLocaleString()}
          sub="Completed payments"
          tone="green"
        />
        <StatCard
          label="Pending"
          value={(summary.pendingBookings || 0).toLocaleString()}
          sub="Awaiting completion"
          tone="yellow"
        />
        <StatCard
          label="Manual"
          value={(summary.manualBookings || 0).toLocaleString()}
          sub="Operator/manual entries"
          tone="blue"
        />
        <StatCard
          label="Revenue"
          value={formatMoney(summary.totalRevenue || 0)}
          sub="Paid + PaidToOperator"
          tone="purple"
        />
      </div>

      {/* Filters block (subtle card) */}
      <div className="rounded-xl border bg-white shadow-sm p-4 mb-4">
        {/* Toolbar: Filters */}
        <div className="grid grid-cols-1 md:grid-cols-8 gap-3">
          <input
            type="date"
            value={filter.date}
            onChange={(e) => {
              setPage(1);
              setFilter((f) => ({ ...f, date: e.target.value }));
            }}
            className="border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            placeholder="Date"
            title="Filter by booking/departure date"
          />
          <input
            type="text"
            value={filter.from}
            onChange={(e) => {
              setPage(1);
              setFilter((f) => ({ ...f, from: e.target.value }));
            }}
            className="border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            placeholder="From"
            title="Route start"
          />
          <input
            type="text"
            value={filter.to}
            onChange={(e) => {
              setPage(1);
              setFilter((f) => ({ ...f, to: e.target.value }));
            }}
            className="border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            placeholder="To"
            title="Route end"
          />
          <input
            type="text"
            value={filter.userEmail}
            onChange={(e) => {
              setPage(1);
              setFilter((f) => ({ ...f, userEmail: e.target.value }));
            }}
            className="border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            placeholder="User Email or Booking No (RB…)"
            title="Type an email or an RB booking number"
          />

          <select
            value={filter.status}
            onChange={(e) => {
              setPage(1);
              setFilter((f) => ({ ...f, status: e.target.value }));
            }}
            className="border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            title="Booking status"
          >
            <option value="">Status: Any</option>
            <option value="Pending">Pending</option>
            <option value="Paid">Paid</option>
            <option value="Manual">Manual</option>
            <option value="PaidToOperator">PaidToOperator</option>
          </select>

          <select
            value={filter.paymentStatus}
            onChange={(e) => {
              setPage(1);
              setFilter((f) => ({ ...f, paymentStatus: e.target.value }));
            }}
            className="border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            title="Payment status"
          >
            <option value="">Payment: Any</option>
            <option value="Pending">Pending</option>
            <option value="Paid">Paid</option>
            <option value="Manual">Manual</option>
            <option value="PaidToOperator">PaidToOperator</option>
          </select>

          {/* Hour-by-hour window */}
          <select
            value={filter.hourStart}
            onChange={(e) => {
              setPage(1);
              setFilter((f) => ({ ...f, hourStart: e.target.value }));
            }}
            className="border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            title="Hour from"
          >
            <option value="">Hour from</option>
            {HOUR_OPTIONS.map((h) => (
              <option key={h.value} value={h.value}>{h.label}</option>
            ))}
          </select>

          <select
            value={filter.hourEnd}
            onChange={(e) => {
              setPage(1);
              setFilter((f) => ({ ...f, hourEnd: e.target.value }));
            }}
            className="border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            title="Hour to"
          >
            <option value="">Hour to</option>
            {HOUR_OPTIONS.map((h) => (
              <option key={h.value} value={h.value}>{h.label}</option>
            ))}
          </select>
        </div>

        {/* Time basis row */}
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <label className="text-sm text-gray-700">Time basis:</label>
          <select
            value={filter.timeBasis}
            onChange={(e) => {
              setPage(1);
              setFilter((f) => ({ ...f, timeBasis: e.target.value }));
            }}
            className="border px-3 py-1.5 rounded focus:outline-none focus:ring-2 focus:ring-blue-500/30"
          >
            <option value="created">Booking Created</option>
            <option value="departure">Departure Time</option>
          </select>
          <span className="text-xs text-gray-500">
            Tip: select a Date + Hour from/to (e.g., 12 PM → 1 PM) to see that window.
          </span>
        </div>

        {/* Quick RB builder */}
        <div className="flex flex-wrap items-end gap-2 mt-4">
          <span className="text-sm font-medium text-gray-700">Quick booking search:</span>
          <input
            value="RB"
            disabled
            className="border px-3 py-2 rounded w-16 bg-gray-100 text-gray-600"
            title="Prefix"
          />
          <input
            type="date"
            value={bnDate}
            onChange={(e) => setBnDate(e.target.value)}
            className="border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            title="Date part"
          />
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="####"
            value={bnSeq}
            onChange={(e) => setBnSeq(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                const rb = buildRB(bnDate, bnSeq);
                if (rb) {
                  setPage(1);
                  // also set date filter to the chosen date to narrow results
                  setFilter((f) => ({ ...f, userEmail: rb, date: bnDate || f.date }));
                }
              }
            }}
            className="border px-3 py-2 rounded w-24 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            title="Sequence (last 4 digits)"
          />
          <button
            className="px-3 py-2 rounded border bg-gray-50 hover:bg-gray-100 transition"
            onClick={() => {
              const rb = buildRB(bnDate, bnSeq);
              if (rb) {
                setPage(1);
                setFilter((f) => ({ ...f, userEmail: rb, date: bnDate || f.date }));
              }
            }}
            title="Apply RB+Date+Number"
          >
            Find
          </button>
          <button
            className="text-sm underline text-gray-600"
            onClick={() => {
              setBnDate("");
              setBnSeq("");
              setPage(1);
              setFilter((f) => ({ ...f, userEmail: "" }));
            }}
          >
            Clear
          </button>
        </div>
      </div>

      {/* Toolbar: Paging */}
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-gray-600">
          {loading ? "Loading…" : `Total: ${total.toLocaleString()}`}
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Rows:</label>
          <select
            className="border px-2 py-1 rounded focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            value={pageSize}
            onChange={(e) => {
              setPage(1);
              setPageSize(Number(e.target.value));
            }}
          >
            {PAGE_SIZES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>

          <button
            className="border px-2 py-1 rounded disabled:opacity-50"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
          >
            Prev
          </button>
          <span className="text-sm text-gray-700">
            Page {page} / {totalPages}
          </span>
          <button
            className="border px-2 py-1 rounded disabled:opacity-50"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || loading}
          >
            Next
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-auto border rounded">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <HeaderCell label="User" field="userName" />
              <HeaderCell label="Email" field="userEmail" />
              <HeaderCell label="Booking No" field="bookingNo" />
              <HeaderCell label="Bus" field="busName" />
              <HeaderCell label="Route" field="routeFrom" />
              <HeaderCell label="Date" field="date" />
              <HeaderCell label="Seats" field="bookingNo" />
              <HeaderCell label="Payment" field="paymentStatus" />
              <HeaderCell label="Status" field="status" />
              <th className="border px-3 py-2 bg-gray-100 sticky top-0 z-10">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: Math.min(pageSize, 10) }).map((_, i) => (
                <tr key={i} className="animate-pulse">
                  {Array.from({ length: 10 }).map((__, j) => (
                    <td key={j} className="border px-3 py-2">
                      <div className="h-3 bg-gray-200 rounded" />
                    </td>
                  ))}
                </tr>
              ))
            ) : err ? (
              <tr>
                <td colSpan={10} className="border px-3 py-6 text-center text-red-700">
                  {err}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={10} className="border px-3 py-6 text-center text-gray-600">
                  No bookings found for your filters.
                </td>
              </tr>
            ) : (
              rows.map((b) => {
                const route =
                  b.bus?.from && b.bus?.to ? `${b.bus.from} → ${b.bus.to}` : "-";

                const dateText = b.date
                  ? `${b.date}${b.departureTime ? ` ${b.departureTime}` : ""}`
                  : "-";

                const seats = Array.isArray(b.selectedSeats)
                  ? b.selectedSeats
                  : [];

                const userName =
                  b.user?.fullName || b.user?.name || "-";

                const userEmail =
                  b.user?.email || "-";

                const bookingStatus = b.status || b.paymentStatus || "-";

                return (
                  <tr
                    key={b._id}
                    className={`hover:bg-gray-50 transition-colors duration-700 ${
                      highlightedRowId === b._id ? "bg-green-50" : ""
                    }`}
                  >
                    <td className="border px-3 py-2">{userName}</td>
                    <td className="border px-3 py-2">{userEmail}</td>
                    <td className="border px-3 py-2 font-semibold text-gray-900">
                      {b.bookingNo || b.bookingNoShort || "-"}
                    </td>
                    <td className="border px-3 py-2">{b.bus?.name || "-"}</td>
                    <td className="border px-3 py-2">{route}</td>
                    <td className="border px-3 py-2">{dateText}</td>
                    <td className="border px-3 py-2">
                      {seats && seats.length ? seats.join(", ") : "-"}
                    </td>
                    <td className="border px-3 py-2">
                      {getPaymentBadge(b.paymentStatus)}
                    </td>
                    <td className="border px-3 py-2">
                      {getStatusBadge(bookingStatus)}
                    </td>
                    <td className="border px-3 py-2 text-center space-x-2">
                      <Link
                        to={`/admin/bookings/${b._id}`}
                        className="inline-block bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 transition-colors duration-200"
                      >
                        View
                      </Link>
                      <button
                        disabled={actionLoading === b._id}
                        className={`px-3 py-1 rounded text-white transition-colors duration-200 ${
                          actionLoading === b._id
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-red-600 hover:bg-red-700"
                        }`}
                        onClick={() => cancelBooking(b._id)}
                      >
                        {actionLoading === b._id ? "Cancelling..." : "Cancel"}
                      </button>
                      <button
                        className="bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600 transition-colors duration-200"
                        onClick={() => {
                          setRescheduleData(b);
                          setNewDate(b.date || "");
                          const s = Array.isArray(b.selectedSeats)
                            ? b.selectedSeats
                            : [];
                          setNewSeats(s.join(", "));
                        }}
                      >
                        Reschedule
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Reschedule Modal (simple inline card for now) */}
      {rescheduleData && (
        <div className="mt-6 p-6 border bg-gray-50 rounded shadow-sm">
          <h3 className="text-lg font-bold mb-2">✏️ Reschedule Booking</h3>
          <p className="text-sm text-gray-700">
            <strong>User:</strong> {rescheduleData.user?.email || "-"}
          </p>
          <p className="text-sm text-gray-700">
            <strong>Bus:</strong> {rescheduleData.bus?.name || "-"}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <div>
              <label className="text-xs text-gray-600">New date</label>
              <input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                className="w-full border px-3 py-2 rounded"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600">New seats (comma-separated)</label>
              <input
                type="text"
                value={newSeats}
                onChange={(e) => setNewSeats(e.target.value)}
                className="w-full border px-3 py-2 rounded"
                placeholder="e.g. 1A, 2B"
              />
            </div>
          </div>

          <div className="flex gap-3 mt-4">
            <button
              className={`bg-blue-600 text-white px-4 py-2 rounded transition-colors duration-200 ${
                rescheduleLoading ? "opacity-70 cursor-not-allowed" : "hover:bg-blue-700"
              }`}
              onClick={confirmReschedule}
              disabled={rescheduleLoading}
            >
              {rescheduleLoading ? "Saving..." : "Confirm"}
            </button>
            <button
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition-colors duration-200"
              onClick={() => setRescheduleData(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Confirm Cancel Modal */}
      {confirmModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-lg w-[90%] max-w-md p-6 transform transition-all duration-200 scale-100">
            <h3 className="text-lg font-semibold mb-2">
              Confirm Cancellation
            </h3>

            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to cancel this booking? This action cannot be undone.
            </p>

            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 transition-colors duration-200"
                onClick={() => setConfirmModal({ open: false, bookingId: null })}
              >
                Cancel
              </button>

              <button
                className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700 transition-colors duration-200"
                onClick={handleConfirmCancel}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBookings;

